import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../api/endpoints';
import { ApiError } from '../api/client';
import type { CustomerCard, RechargeRequest } from '../api/types';
import { usePoll } from '../hooks/usePoll';
import { elapsedSince, formatMinutes, formatMoney, POOL_LABEL } from '../lib/format';
import { chime, readMuted } from './OrderAlerts';
import { SellRechargeDialog } from './SellRechargeDialog';
import { Button } from './ui';
import { useToast } from './Toast';

// A customer asked for a recharge from the HHG app. Like a station order it
// sits in the app shell until someone deals with it, but it is quieter: the
// customer is usually not waiting at a seat with the clock running, so it
// chimes once when it arrives (sharing the orders' mute) and does not nag.
//
// "Sell" opens the normal sell dialog with the pack they asked for picked
// already, so payment, holds and adjustments all work as at the counter. The
// sale carries the request id; the backend marks the request done in the
// same transaction, and refuses if another dashboard already did.

const POLL_MS = 6000;

export function RechargeRequestAlerts() {
  const toast = useToast();
  const navigate = useNavigate();
  const { data, refresh } = usePoll(
    (signal) => api.recharges.requests('pending', signal),
    POLL_MS,
  );
  const requests = data ?? [];

  const [busy, setBusy] = useState<string | null>(null);
  const [selling, setSelling] = useState<{ request: RechargeRequest; customer: CustomerCard } | null>(null);

  const seen = useRef<Set<string>>(new Set());
  useEffect(() => {
    let fresh = false;
    for (const request of requests) {
      if (!seen.current.has(request.request_id)) {
        seen.current.add(request.request_id);
        fresh = true;
      }
    }
    if (fresh && !readMuted()) chime();
  }, [requests]);

  const sell = async (request: RechargeRequest): Promise<void> => {
    setBusy(request.request_id);
    try {
      const customer = await api.customers.get(request.customer_id);
      setSelling({ request, customer });
    } catch (err) {
      toast.error((err as Error).message);
    } finally {
      setBusy(null);
    }
  };

  const decline = async (request: RechargeRequest): Promise<void> => {
    setBusy(request.request_id);
    try {
      await api.recharges.declineRequest(request.request_id);
      toast.info(`${request.customer_name}'s request declined. Their app tells them to ask at the counter.`);
    } catch (err) {
      if (err instanceof ApiError && err.status === 409) toast.info(err.message);
      else toast.error((err as Error).message);
    } finally {
      setBusy(null);
      refresh();
    }
  };

  if (requests.length === 0 && !selling) return null;

  return (
    <>
      {requests.length > 0 && (
        <div
          role="status"
          aria-live="polite"
          className="border-t border-emerald-500/40 bg-emerald-950/60 px-4 py-3 backdrop-blur"
        >
          <p className="mb-2 flex items-center gap-2 text-sm font-semibold text-emerald-100">
            <span className="inline-flex h-2.5 w-2.5 rounded-full bg-emerald-400" />
            {requests.length === 1
              ? 'A customer asked for a recharge'
              : `${requests.length} customers asked for a recharge`}
          </p>
          <ul className="flex flex-col gap-2">
            {requests.map((request) => (
              <li
                key={request.request_id}
                className="flex flex-wrap items-center gap-x-4 gap-y-2 rounded-lg border border-emerald-400/30 bg-slate-950/60 px-4 py-3"
              >
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-baseline gap-x-2">
                    <button
                      onClick={() => navigate(`/customers/${request.customer_id}`)}
                      className="text-lg font-bold text-white hover:underline"
                    >
                      {request.customer_name}
                    </button>
                    <span className="text-xs text-slate-400">{request.customer_phone}</span>
                    <span className="text-xs text-slate-400">{age(request.requested_at)}</span>
                  </div>
                  <p className="mt-0.5 text-sm text-slate-100">
                    {request.label} · {POOL_LABEL[request.pool]} · {formatMinutes(request.minutes)}
                    <span className="ml-2 font-mono font-semibold text-emerald-100">
                      {formatMoney(request.price)}
                    </span>
                  </p>
                </div>
                <div className="flex gap-2">
                  <Button
                    variant="secondary"
                    onClick={() => void decline(request)}
                    disabled={busy === request.request_id}
                  >
                    Decline
                  </Button>
                  <Button onClick={() => void sell(request)} disabled={busy === request.request_id}>
                    {busy === request.request_id ? 'Working…' : 'Sell'}
                  </Button>
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}

      {selling && (
        <SellRechargeDialog
          customer={selling.customer}
          initialPool={selling.request.pool}
          initialPackId={selling.request.pricing_rule_id}
          requestId={selling.request.request_id}
          onClose={() => setSelling(null)}
          onSold={refresh}
        />
      )}
    </>
  );
}

/** "just now", then "3m ago". */
function age(iso: string): string {
  const elapsed = elapsedSince(iso);
  return elapsed === '0m' ? 'just now' : `${elapsed} ago`;
}
